import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./context/AuthContext";

// Layout components
import Layout from "./components/layout/Layout";

// Pages
import EnhancedDashboard from "./pages/dashboard/EnhancedDashboard";
import ProspectsList from "./pages/prospects/ProspectsList";
import EnhancedAthleteProfile from "./pages/prospects/profile/EnhancedAthleteProfile";
import AddProspect from "./pages/prospects/AddProspect";
import ImportProspects from "./pages/prospects/ImportProspects";
import DepthChartEditor from "./pages/depthchart/DepthChartEditor";
import CoachDatabase from "./pages/coaches/CoachDatabase";
import BoardsPage from "./pages/boards/BoardsPage";
import SheetsPage from "./pages/sheets/SheetsPage";
import AthleteSearchResults from "./pages/search/results/AthleteSearchResults";
import Login from "./pages/auth/EnhancedLogin";
import Register from "./pages/auth/EnhancedRegister";
import Profile from "./pages/auth/Profile";
import NotFound from "./pages/NotFound";

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#002B5C", // TeamScout navy blue
      light: "#1A4980",
      dark: "#001F42",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#0077B6", // TeamScout blue
      light: "#3395CA",
      dark: "#005A8C",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f5f5",
      paper: "#ffffff",
    },
    info: {
      main: "#0077B6", // TeamScout blue
    },
    success: {
      main: "#2E7D32",
    },
    warning: {
      main: "#ED6C02",
    },
    error: {
      main: "#D32F2F",
    },
  },
  typography: {
    fontFamily: ["Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    h1: {
      fontWeight: 700,
      color: "#002B5C",
    },
    h2: {
      fontWeight: 700,
      color: "#002B5C",
    },
    h3: {
      fontWeight: 600,
      color: "#002B5C",
    },
    h4: {
      fontWeight: 600,
      color: "#002B5C",
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: "auto",
        },
        maxWidthXl: {
          "@media (min-width:1536px)": {
            maxWidth: "calc(100% - 48px)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#002B5C",
        },
      },
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <div>Loading...</div>;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Redirect component for prospect details
const ProspectDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/prospects/profile/${id}`} replace />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<EnhancedDashboard />} />
            <Route path="sheets" element={<SheetsPage />} />
            <Route path="prospects">
              <Route index element={<ProspectsList />} />
              <Route path="add" element={<AddProspect />} />
              <Route path="import" element={<ImportProspects />} />
              <Route path=":id" element={<ProspectDetailRedirect />} />
              <Route path="profile/:id" element={<EnhancedAthleteProfile />} />
            </Route>
            <Route path="depth-chart">
              <Route
                index
                element={<Navigate to="/depth-chart/new" replace />}
              />
              <Route path=":id" element={<DepthChartEditor />} />
            </Route>
            <Route path="coaches" element={<CoachDatabase />} />
            <Route path="boards" element={<BoardsPage />} />
            <Route
              path="advanced-search"
              element={<Navigate to="/search/results" replace />}
            />
            <Route path="search/results" element={<AthleteSearchResults />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
